/**
 * The nouns this app spells once.
 *
 * A word like "record" or "field" appears in dozens of sentences, and every
 * copy is a place the next translator can spell it differently. The glossary
 * is the one spelling: a value references it as `$t(glossary.record_other)`
 * and code asks for it as `t('glossary.record', { count })`.
 *
 * **Every noun here is lower case, because the glossary holds the MID-SENTENCE
 * form.** *Writing in the UI* — "only a name keeps its capitals" — decides it,
 * and the direction is forced: a nested `$t()` is dropped into a sentence
 * wherever the sentence happens to say the word, so a capital inside one would
 * read as "Give them a Job" at every site but the first. A label that is
 * nothing but the noun — the Jobs screen's title, the Job row on a person —
 * keeps its own key with its own capital (`job.title`, `job.section`), because
 * a label's first letter is capitalised by being first and not by being that
 * word. `dashboard` is the single exception: it is the Dashboard tab named by
 * its own label, which is one of the four classes `writing.enforcement.test.ts`
 * exempts, and `tabs.dashboard` IS "Dashboard".
 *
 * **`insight`, not `card`.** The owner settled it on 19 Sep 2026: the object a
 * person puts on top of a record is an insight, which is what the 179-key
 * `insight.` domain has always called it. `glossary.card_*` was born in Phase 1
 * out of `setup.words.cards.*`, never shipped, and is renamed here rather than
 * kept — the rule that a key never changes protects keys in the store and in
 * tenants' rows, and this was neither.
 *
 * **A record and a list are two things, not two words for one.** A record holds
 * money and events; a list is a directory — Customers, Items, Suppliers. The
 * catalogue already argued it: `setup.plan.no_money_in_list` and
 * `search.records_and_lists` say both nouns in one breath.
 *
 * **`business` says workspace** (web redesign stage 1, Decided 16, 1 Oct
 * 2026): the tenant a person opens is a workspace, on the web and the phone.
 * The key keeps its old word, as keys do. A value that means the person's own
 * trade, not the tenant, spells "business" itself rather than nesting this
 * term. Setup's questions about the workspace being made nest it ("what your
 * workspace is called", web redesign stage 7b, Decided 5); its first question
 * asks about their work, not the workspace (r3 Setup, item 15).
 *
 * Every term is a pair, because a glossary noun is nearly always said with a
 * count. `dashboard` is the one that is not: it is a name, and there is one.
 *
 * It is one object so both namespaces can spread the same source — the server
 * needs the same nouns inside `server` for `$t()` to resolve without a
 * namespace prefix, and a test proves the two copies are equal.
 */
export const GLOSSARY = {
  // What a person keeps. A record holds entries; a list holds rows.
  record_one: 'record',
  record_other: 'records',
  list_one: 'list',
  list_other: 'lists',
  entry_one: 'entry',
  entry_other: 'entries',
  row_one: 'row',
  row_other: 'rows',
  field_one: 'field',
  field_other: 'fields',
  link_one: 'link',
  link_other: 'links',
  insight_one: 'insight',
  insight_other: 'insights',
  recipe_one: 'recipe',
  recipe_other: 'recipes',
  // Who keeps it, and what they may do.
  business_one: 'workspace',
  business_other: 'workspaces',
  person_one: 'person',
  person_other: 'people',
  job_one: 'job',
  job_other: 'jobs',
  level_one: 'level',
  level_other: 'levels',
  subscription_one: 'subscription',
  subscription_other: 'subscriptions',
  // What RecordOS does by itself, on a date or a schedule: a monthly run, a task rule, an email rule (docs/plans/routines.md, R2).
  routine_one: 'routine',
  routine_other: 'routines',
  // A name, so it keeps its capital and has no plural.
  dashboard: 'Dashboard',
} as const;

/** `record_one` becomes `glossary.record_one`, with the literal type kept. */
type Prefixed<T> = { readonly [K in keyof T & string as `glossary.${K}`]: T[K] };

/**
 * The same pairs under the `glossary.` prefix, which is the shape a catalogue
 * spreads. Written by a function rather than by hand so the words have one
 * home: a term added above cannot be forgotten here, and both namespaces get
 * the same object out of the same call.
 */
export const glossaryWords = <T extends Readonly<Record<string, string>>>(source: T): Prefixed<T> =>
  Object.fromEntries(
    Object.entries(source).map(([term, word]) => [`glossary.${term}`, word]),
  ) as Prefixed<T>;

/** The glossary as catalogue keys. `en.ts` spreads this; `apps/server` will too. */
export const GLOSSARY_WORDS = glossaryWords(GLOSSARY);
